import {
    GetProjectCatalogDocument,
    MeasuringPointSensorAction,
    OverviewDetachSensorFromMeasuringPointDocument,
    RequestMeasuringPointSensorActionDocument,
    type RequestMeasuringPointSensorActionMutationVariables,
    SetMeasuringPointArchivedDocument,
    SoftDeleteMeasuringPointDocument,
    UndoMyMeasuringPointShareDocument,
    type PastSensorType,
} from '@/graphql/types';
import type { MeasuringPointWithProject } from '../../types';
import type { OptionsMenuItem } from '../../components/OptionsMenu';
import { PERMISSION } from '../../../sharing/utils';
import { createEditUrl } from '../../../measuringPoint/utils';
import { confirm } from '../../../../confirm/Confirm';
import { addNotificationHack, NOTIFICATION_VARIANT } from '../../../../notifications/Notifications';
import { getClient } from '../../../../../utils/apollo-client';
import warning from '../../../../../utils/logger';
import { ObjectType } from '../../../../../utils/objectType';
import { schema } from '../../../../../schema';
import { createAttributesOverviewPath } from '../../../attributes/routeUtils';
import {
    buildArchiveMenuSpec,
    buildDeleteMenuSpec,
    buildMenuItems,
    buildUndoSharingMenuSpec,
    confirmAndRunMutation,
    copyLabel,
    getOverviewRefetchQueriesForMeasuringPoint,
    getDetachMessage,
    getRestartMessage,
    handleCopyAction,
    runMutationWithRefresh,
} from './menuItemActions';

const PROJECT = ObjectType.PROJECT.type;
const MEASURING_POINT = ObjectType.MEASURING_POINT.type;

type MeasuringPointWithAttachedSensor = MeasuringPointWithProject & {
    sensor: NonNullable<MeasuringPointWithProject['sensor']>;
};

function hasAttachedSensor(
    measuringPoint: MeasuringPointWithProject
): measuringPoint is MeasuringPointWithAttachedSensor {
    return measuringPoint.sensor != null;
}

function handleDetachAction(measuringPoint: MeasuringPointWithAttachedSensor) {
    confirm({
        markdownMessage: getDetachMessage(measuringPoint.sensor.name, measuringPoint.name),
        onConfirm() {
            void (async () => {
                try {
                    await getClient().mutate({
                        mutation: OverviewDetachSensorFromMeasuringPointDocument,
                        variables: { id: measuringPoint.id },
                        // The row updates from the mutation result; the project's alert and
                        // status counts come with the catalog page, as its summaries.
                        refetchQueries: [GetProjectCatalogDocument],
                    });

                    addNotificationHack({
                        message: gettext('SENSOR_DETACHED_SUCCESSFULLY'),
                        variant: NOTIFICATION_VARIANT.SUCCESS,
                        scrollToTop: true,
                    });
                } catch (error) {
                    addNotificationHack({
                        message: gettext('SENSOR_DETACH_FAILED'),
                        variant: NOTIFICATION_VARIANT.ERROR,
                        scrollToTop: true,
                    });
                    warning({ message: error });
                }
            })();
        },
    });
}

export function createMeasuringPointMenuItems(
    measuringPoint: MeasuringPointWithProject,
    archived: boolean
): OptionsMenuItem[] {
    const { permission } = measuringPoint;
    const measuringPointId = Number(measuringPoint.id);
    const demoMeasuringPointIds = schema.demoMeasuringPoints.all as readonly number[];
    const isDemoMp = demoMeasuringPointIds.includes(measuringPointId);
    const refetchQueries = getOverviewRefetchQueriesForMeasuringPoint(
        measuringPoint.projectId,
        archived
    );

    const menuSpecs = [
        {
            label: gettext('CONFIGURATION'),
            to: createEditUrl(measuringPoint.id, measuringPoint.swarmType, false),
            requiredPermissions: PERMISSION.FullAccess,
            showWhenDemoMp: true,
        },
        {
            label: gettext('REPORTS'),
            href: `/reports/edit/${measuringPoint.id}`,
            requiredPermissions: PERMISSION.FullAccess,
            showWhenDemoMp: true,
        },
        {
            label: gettext('ATTRIBUTES'),
            to: createAttributesOverviewPath('measuring_point', measuringPoint.id),
            requiredPermissions: PERMISSION.FullAccess,
            showWhenDemoMp: true,
        },
        {
            label: gettext('SHARE_MEASURING_POINT'),
            to: `/sharing/share-measuring-point-edit/${measuringPoint.id}`,
            requiredPermissions: PERMISSION.FullAccess,
            showWhenDemoMp: true,
        },
        buildArchiveMenuSpec({
            type: MEASURING_POINT,
            id: measuringPoint.id,
            objectName: measuringPoint.name,
            archived: measuringPoint.archived,
            requiredPermissions: PERMISSION.FullAccess,
            mutation: SetMeasuringPointArchivedDocument,
            refetchQueries,
        }),
        buildDeleteMenuSpec({
            type: MEASURING_POINT,
            id: measuringPoint.id,
            objectName: measuringPoint.name,
            requiredPermissions: PERMISSION.FullAccess,
            mutation: SoftDeleteMeasuringPointDocument,
            refetchQueries,
        }),
        {
            label: copyLabel(MEASURING_POINT),
            requiredPermissions: PERMISSION.FullAccess,
            onClick: () =>
                handleCopyAction({
                    id: measuringPoint.id,
                    swarmType: measuringPoint.swarmType,
                    source: MEASURING_POINT,
                    destination: MEASURING_POINT,
                    refetchQueries,
                }),
        },
        {
            label: copyLabel(PROJECT),
            requiredPermissions: PERMISSION.FullAccess,
            onClick: () =>
                handleCopyAction({
                    id: measuringPoint.id,
                    swarmType: measuringPoint.swarmType,
                    source: MEASURING_POINT,
                    destination: PROJECT,
                }),
        },
        ...(hasAttachedSensor(measuringPoint)
            ? (() => {
                  const canRotate = measuringPoint.sensor.isRotating !== true;
                  const canRestart = measuringPoint.sensor.isRestarting !== true;
                  const rotateAction: RequestMeasuringPointSensorActionMutationVariables['action'] =
                      MeasuringPointSensorAction.Rotate;
                  const restartAction: RequestMeasuringPointSensorActionMutationVariables['action'] =
                      MeasuringPointSensorAction.Restart;

                  return [
                      {
                          label: gettext('DETACH'),
                          requiredPermissions: PERMISSION.FullAccess,
                          onClick: () => handleDetachAction(measuringPoint),
                      },
                      canRotate
                          ? {
                                label: gettext('ALIGN_AXIS'),
                                requiredPermissions: PERMISSION.FullAccess,
                                onClick: () =>
                                    runMutationWithRefresh({
                                        mutation: RequestMeasuringPointSensorActionDocument,
                                        variables: {
                                            id: measuringPoint.id,
                                            action: rotateAction,
                                        },
                                        successMessage: gettext(
                                            'OVERVIEW_SENSOR_ALIGN_AXIS_SUCCESS'
                                        ),
                                        refetchQueries,
                                    }),
                            }
                          : {
                                label: gettext('ALIGN_AXIS_IN_PROGRESS'),
                                requiredPermissions: PERMISSION.FullAccess,
                                disabled: true,
                            },
                      canRestart
                          ? {
                                label: gettext('RESTART'),
                                requiredPermissions: PERMISSION.FullAccess,
                                onClick: () =>
                                    confirmAndRunMutation(
                                        getRestartMessage(measuringPoint.sensor.name),
                                        RequestMeasuringPointSensorActionDocument,
                                        {
                                            id: measuringPoint.id,
                                            action: restartAction,
                                        },
                                        gettext('OVERVIEW_SENSOR_RESTART_SUCCESS'),
                                        refetchQueries
                                    ),
                            }
                          : {
                                label: gettext('RESTART_IN_PROGRESS'),
                                requiredPermissions: PERMISSION.FullAccess,
                                disabled: true,
                            },
                  ];
              })()
            : []),
        ...(measuringPoint.directShare
            ? [
                  buildUndoSharingMenuSpec({
                      type: MEASURING_POINT,
                      id: measuringPoint.id,
                      objectName: measuringPoint.name,
                      requiredPermissions: PERMISSION.View,
                      mutation: UndoMyMeasuringPointShareDocument,
                      // Debug-only context for internal warning logs (not user-facing UI text).
                      actionLabel: 'undo measuring point sharing',
                      variablesFromNumericId: (id) => ({ measuringPointId: id }),
                      refetchQueries,
                  }),
              ]
            : []),
        ...(measuringPoint.pastSensors ?? [])
            .filter((sensor): sensor is PastSensorType => Boolean(sensor?.calibrationReportUrl))
            .map((sensor) => ({
                label: `${sensor.simpleName ?? gettext('UNKNOWN')} ${gettext(
                    'CALIBRATION_REPORT'
                )}`,
                href: sensor.calibrationReportUrl,
                requiredPermissions: [PERMISSION.View, PERMISSION.FullAccess],
            })),
    ];

    return buildMenuItems(menuSpecs, permission, { isDemoMp });
}
